import { GitMergeIcon, ListFilterIcon } from 'lucide-react';
import { useCallback, useRef, useState, useEffect } from 'react';

import { Kbd } from '@/components/kbd';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { StateCell } from '@/features/pulls/components/pull-list';
import { FilterBadge } from '@/features/pulls/components/pull-list/filters/filter-badge';
import { usePullListFilterShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { Filter, FilterField } from '@/types/filters';

const STATE_OPTIONS: { value: 'OPEN' | 'CLOSED' | 'MERGED' | 'DRAFT'; label: string }[] = [
  { value: 'OPEN' as const, label: 'Open' },
  { value: 'DRAFT' as const, label: 'Draft' },
  { value: 'MERGED' as const, label: 'Merged' },
  { value: 'CLOSED' as const, label: 'Closed' },
];

export const PullsFilter = ({
  repositories,
  authors,
  filters,
  setFilters,
  open,
  onOpenChange,
  onFilterBadgeRef,
  reviewers,
}: {
  repositories: string[];
  authors: { login: string; name?: string | null; avatarUrl?: string }[];
  filters: Filter[];
  setFilters: (filters: Filter[] | ((filters: Filter[]) => Filter[])) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onFilterBadgeRef?: (field: string, el: HTMLDivElement | null) => void;
  reviewers?: { login: string; avatarUrl?: string }[];
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [openSubmenu, setOpenSubmenu] = useState<'state' | 'repo' | 'author' | 'reviewer' | 'readyToMerge' | null>(null);
  const filterBadgeRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Refs for submenu triggers
  const stateSubmenuRef = useRef<HTMLDivElement>(null);
  const repoSubmenuRef = useRef<HTMLDivElement>(null);
  const authorSubmenuRef = useRef<HTMLDivElement>(null);
  const reviewerSubmenuRef = useRef<HTMLDivElement>(null);
  const readyToMergeSubmenuRef = useRef<HTMLDivElement>(null);

  // Use the filter shortcuts hook when the dropdown is open
  usePullListFilterShortcuts({
    filters,
    setFilters,
    enabled: open,
    openSubmenu,
    setOpenSubmenu,
    onOpenStateSubmenu: () => {
      setOpenSubmenu('state');
      // Use setTimeout to ensure the DOM is updated before triggering click
      setTimeout(() => stateSubmenuRef.current?.click(), 0);
    },
    onOpenRepoSubmenu: () => {
      setOpenSubmenu('repo');
      setTimeout(() => repoSubmenuRef.current?.click(), 0);
    },
    onOpenAuthorSubmenu: () => {
      setOpenSubmenu('author');
      setTimeout(() => authorSubmenuRef.current?.click(), 0);
    },
    onOpenReviewerSubmenu: () => {
      setOpenSubmenu('reviewer');
      setTimeout(() => reviewerSubmenuRef.current?.click(), 0);
    },
    onOpenReadyToMergeSubmenu: () => {
      setOpenSubmenu('readyToMerge');
      setTimeout(() => readyToMergeSubmenuRef.current?.click(), 0);
    },
  });

  // Reset submenu when dropdown closes
  useEffect(() => {
    if (!open) {
      setOpenSubmenu(null);
    }
  }, [open]);

  const toggleFilterValue = (field: FilterField, value: string) => {
    // If the filter doesn't exist, add it
    const existingFilter = filters.find((f) => f.field === field);
    if (!existingFilter) {
      setFilters((prev) => [...prev, { field, operation: 'IN', values: [value] }]);
      return;
    }

    // If the value already exists, remove it
    if (existingFilter.values.includes(value)) {
      setFilters((prev) =>
        prev.map((f) => (f.field === field ? { ...f, values: f.values.filter((v) => v !== value) } : f))
      );
      return;
    }

    // Otherwise, add the value
    setFilters((prev) => prev.map((f) => (f.field === field ? { ...f, values: [...f.values, value] } : f)));
  };

  const AuthorOption = useCallback(
    ({ value }: { value: string; label: string }) => {
      const author = authors.find((a) => a.login === value);
      return (
        <div className="flex items-center gap-2">
          <img src={author?.avatarUrl} alt={`${author?.login}'s avatar`} className="size-4 rounded-full" />
          <span className="text-sm truncate">{author?.login}</span>
        </div>
      );
    },
    [authors]
  );

  const ReviewerOption = useCallback(
    ({ value }: { value: string; label: string }) => {
      const reviewer = reviewers?.find((r) => r.login === value);
      return (
        <div className="flex items-center gap-2">
          <img src={reviewer?.avatarUrl} alt={`${reviewer?.login}'s avatar`} className="size-4 rounded-full" />
          <span className="text-sm truncate">{reviewer?.login}</span>
        </div>
      );
    },
    [reviewers]
  );

  const handleFilterBadgeRef = useCallback(
    (field: string, el: HTMLDivElement | null) => {
      if (el) {
        filterBadgeRefs.current.set(field, el);
      } else {
        filterBadgeRefs.current.delete(field);
      }
      // Also call the parent's ref handler if provided
      onFilterBadgeRef?.(field, el);
    },
    [onFilterBadgeRef]
  );

  return (
    <div className="flex items-center gap-2 text-sm">
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            variant="ghost"
            className="flex items-center gap-2 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground"
          >
            <ListFilterIcon className="size-4" />
            Filter
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[240px]">
          <DropdownMenuItem
            onClick={() => setFilters([])}
            className="px-4 py-2 flex items-center"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                setFilters([]);
              }
            }}
          >
            <div className="flex items-center justify-between w-full flex-1">
              <span>Clear All Filters</span>
              <Kbd>c</Kbd>
            </div>
            {/* <ChevronRightIcon className="size-4" /> */}
          </DropdownMenuItem>
          <FiltersDropdownSub
            header="State"
            options={STATE_OPTIONS}
            renderOption={StateOption}
            values={filters.find((f) => f.field === 'state')?.values || []}
            toggleValue={(value) => toggleFilterValue('state', value)}
            openSubmenu={openSubmenu === 'state'}
            setOpenSubmenu={() => setOpenSubmenu('state')}
            triggerRef={stateSubmenuRef}
          />
          <FiltersDropdownSub
            header="Repository"
            options={repositories.map((repo) => ({ value: repo, label: repo }))}
            renderOption={RepoOption}
            values={filters.find((f) => f.field === 'repo')?.values || []}
            toggleValue={(value) => toggleFilterValue('repo', value)}
            openSubmenu={openSubmenu === 'repo'}
            setOpenSubmenu={() => setOpenSubmenu('repo')}
            triggerRef={repoSubmenuRef}
          />
          <FiltersDropdownSub
            header="Author"
            options={authors.map((author) => ({ value: author.login, label: author.login }))}
            renderOption={AuthorOption}
            values={filters.find((f) => f.field === 'author')?.values || []}
            toggleValue={(value) => toggleFilterValue('author', value)}
            openSubmenu={openSubmenu === 'author'}
            setOpenSubmenu={() => setOpenSubmenu('author')}
            triggerRef={authorSubmenuRef}
          />
          <FiltersDropdownSub
            header="Reviewer"
            options={reviewers?.map((reviewer) => ({ value: reviewer.login, label: reviewer.login })) || []}
            renderOption={ReviewerOption}
            values={filters.find((f) => f.field === 'reviewer')?.values || []}
            toggleValue={(value) => toggleFilterValue('reviewer', value)}
            openSubmenu={openSubmenu === 'reviewer'}
            setOpenSubmenu={() => setOpenSubmenu('reviewer')}
            triggerRef={reviewerSubmenuRef}
          />
          <FiltersDropdownSub
            header="Ready to Merge"
            options={[
              { value: 'true', label: 'Ready' },
              { value: 'false', label: 'Not ready' }
            ]}
            renderOption={ReadyToMergeOption}
            values={filters.find((f) => f.field === 'readyToMerge')?.values || []}
            toggleValue={(value) => toggleFilterValue('readyToMerge', value)}
            openSubmenu={openSubmenu === 'readyToMerge'}
            setOpenSubmenu={() => setOpenSubmenu('readyToMerge')}
            triggerRef={readyToMergeSubmenuRef}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="flex gap-2 items-center">
        {filters.map((filter, index) => {
          const options =
            filter.field === 'state'
              ? STATE_OPTIONS
              : filter.field === 'repo'
                ? repositories.map((repo) => ({ value: repo, label: repo }))
                : filter.field === 'author'
                  ? authors.map((author) => ({ value: author.login, label: author.login }))
                  : filter.field === 'readyToMerge'
                    ? [{ value: 'true', label: 'Ready' }, { value: 'false', label: 'Not ready' }]
                    : reviewers?.map((reviewer) => ({ value: reviewer.login, label: reviewer.login })) || [];
          const renderOption =
            filter.field === 'state' 
              ? StateOption 
              : filter.field === 'repo' 
                ? RepoOption 
                : filter.field === 'author'
                  ? AuthorOption
                  : filter.field === 'reviewer'
                    ? ReviewerOption
                    : ReadyToMergeOption;

          return (
            <FilterBadge
              key={filter.field}
              ref={(el: HTMLDivElement | null) => handleFilterBadgeRef(filter.field, el)}
              field={filter.field}
              values={filter.values}
              renderOption={renderOption}
              authors={authors}
              reviewers={reviewers}
              operation={filter.operation}
              onOperationChange={(operation) =>
                setFilters((prev) => prev.map((f) => (f.field === filter.field ? { ...f, operation } : f)))
              }
              onToggleValue={(value) => toggleFilterValue(filter.field, value)}
              onRemove={() => setFilters((prev) => prev.filter((f) => f.field !== filter.field))}
              options={options}
              tabIndex={index === 0 ? 0 : -1}
              onEscape={() => {
                // Blur the current filter badge to unfocus filters
                const currentEl = filterBadgeRefs.current.get(filter.field);
                currentEl?.blur();
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  const filterFields = filters.map((f) => f.field);
                  const currentIndex = filterFields.indexOf(filter.field);

                  if (e.shiftKey) {
                    // Move to previous filter
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : filterFields.length - 1;
                    const prevField = filterFields[prevIndex];
                    const prevEl = filterBadgeRefs.current.get(prevField);
                    prevEl?.focus();
                  } else {
                    // Move to next filter
                    const nextIndex = currentIndex < filterFields.length - 1 ? currentIndex + 1 : 0;
                    const nextField = filterFields[nextIndex];
                    const nextEl = filterBadgeRefs.current.get(nextField);
                    nextEl?.focus();
                  }
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const FiltersDropdownSub = ({
  header,
  options,
  renderOption,
  values,
  toggleValue,
  openSubmenu,
  setOpenSubmenu,
  triggerRef,
}: {
  header: string;
  options: { value: string; label: string }[];
  renderOption: ({ value, label }: { value: string; label: string }) => React.ReactNode;
  values: string[];
  toggleValue: (value: string) => void;
  openSubmenu: boolean;
  setOpenSubmenu: () => void;
  triggerRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const shortcutKey = header === 'State' ? 's' : header === 'Repository' ? 'r' : header === 'Author' ? 'a' : header === 'Reviewer' ? 'g' : 'm';

  return (
    <DropdownMenuSub open={openSubmenu} onOpenChange={(open) => open && setOpenSubmenu()}>
      <DropdownMenuSubTrigger ref={triggerRef} className="px-4 py-2 flex items-center justify-between flex-1">
        <div className="flex items-center justify-between w-full">
          <span>{header}</span>
          <Kbd>{shortcutKey}</Kbd>
        </div>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-[220px]">
        <FilterDropdownItems
          options={options}
          renderOption={renderOption}
          values={values}
          toggleValue={toggleValue}
          showShortcuts={openSubmenu}
          shortcutType={header === 'State' ? 'state' : 'number'}
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

export const FilterDropdownItems = ({
  options,
  renderOption,
  values,
  toggleValue,
  showShortcuts,
  shortcutType,
}: {
  options: { value: string; label: string }[];
  renderOption: ({ value, label }: { value: string; label: string }) => React.ReactNode;
  values: string[];
  toggleValue: (value: string) => void;
  showShortcuts?: boolean;
  shortcutType?: 'state' | 'number';
}) => {
  const getShortcut = (value: string, index: number) => {
    if (!showShortcuts) return null;

    if (shortcutType === 'state') {
      switch (value) {
        case 'OPEN':
          return 'o';
        case 'DRAFT':
          return 'd';
        case 'MERGED':
          return 'm';
        case 'CLOSED':
          return 'c';
        default:
          return null;
      }
    }
    return null;
  };

  return options.map(({ value, label }, index) => {
    const shortcut = getShortcut(value, index);
    return (
      <DropdownMenuItem
        key={value}
        className="flex items-center gap-2 justify-between cursor-pointer px-3 py-1.5"
        onSelect={(e) => {
          e.preventDefault();
          toggleValue(value);
        }}
        onKeyDown={(e) => {
          // Handle Enter key to toggle the filter
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            toggleValue(value);
          }
        }}
      >
        <div className="flex items-center gap-3">
          <Checkbox checked={values.includes(value)} />
          <div className="flex items-center gap-2">{renderOption({ value, label })}</div>
        </div>
        {shortcut && <Kbd className="ml-2">{shortcut}</Kbd>}
      </DropdownMenuItem>
    );
  });
};

const StateOption = ({ value, label }: { value: string; label: string }) => (
  <div className="flex items-center gap-2">
    <StateCell state={value as 'OPEN' | 'CLOSED' | 'MERGED' | 'DRAFT'} />
    <span className="text-sm truncate">{label}</span>
  </div>
);

const RepoOption = ({ label }: { value: string; label: string }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm truncate">{label}</span>
  </div>
);

const ReadyToMergeOption = ({ label }: { value: string; label: string }) => (
  <div className="flex items-center gap-2">
    <GitMergeIcon className="size-4 text-muted-foreground" />
    <span className="text-sm truncate">{label}</span>
  </div>
);
